/**
 * FeaturePage — /features/{id} 공개 마케팅 랜딩 공통 템플릿 v2.
 *
 * 개편 내용 (2026-06):
 * - Metrics Bar (Hero 아래 숫자 3개)
 * - Intro 이미지: 기능별 고유 이미지 (/features/{slug}-intro.png)
 * - Demo Section: 실제 결과물 미리보기 (pre-generated)
 * - Comparison Table: 수기 vs ChatGPT vs Heinrich
 * - 그룹별 accent 색상 실제 적용
 * - Trust 공통 섹션 제거
 * SEO: Helmet + JSON-LD (FAQPage + Service + SoftwareApplication + HowTo + BreadcrumbList)
 */
import { useMemo, type ComponentType } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  FiArrowRight, FiCheck, FiBookOpen, FiZap,
  FiFileText, FiCpu, FiRefreshCw, FiShield,
} from 'react-icons/fi'
import ForceDarkTheme from '../common/ForceDarkTheme'
import Brand, { renderWithBrand } from '../brand/Brand'
import {
  FeatureDemo, SafetyEduShowcase,
  JSADemoResult, TBMDemoResult, RiskAssessmentDemoResult, ChatbotDemoResult,
  WorkPermitDemoResult, AccidentReportDemoResult, CorrectiveDemoResult,
  ETADemoResult, FTADemoResult, SafetyCostDemoResult, SafetyEduDemoResult,
  ImageAnalysisDemoResult,
} from './FeatureDemo'

const inView = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-48px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
}

export interface FeaturePageProps {
  id: string
  slug: string
  HeroIcon: ComponentType<{ size?: number; className?: string }>
  accent: 'blue' | 'violet' | 'emerald' | 'amber' | 'rose' | 'orange' | 'red' | 'indigo' | 'purple'
  featureIcons?: ComponentType<{ size?: number; className?: string }>[]
  related?: { slug: string; labelKey: string }[]
  demoWidget?: React.ReactNode
  fullWidthDemo?: React.ReactNode
}

const ACCENT_PALETTE: Record<string, {
  orb: string; chip: string; iconColor: string; gradBg: string; border: string; badgeText: string
}> = {
  violet:  { orb:'rgba(139,92,246,0.14)',  chip:'bg-violet-500/10 text-white border-violet-500/20',  iconColor:'text-violet-400',  gradBg:'from-violet-600/10',  border:'border-violet-500/30',  badgeText:'text-violet-400' },
  amber:   { orb:'rgba(245,158,11,0.14)',  chip:'bg-amber-500/10 text-white border-amber-500/20',   iconColor:'text-amber-400',   gradBg:'from-amber-600/10',   border:'border-amber-500/30',   badgeText:'text-amber-400' },
  purple:  { orb:'rgba(168,85,247,0.14)',  chip:'bg-purple-500/10 text-white border-purple-500/20', iconColor:'text-purple-400',  gradBg:'from-purple-600/10',  border:'border-purple-500/30',  badgeText:'text-purple-400' },
  orange:  { orb:'rgba(249,115,22,0.14)',  chip:'bg-orange-500/10 text-white border-orange-500/20', iconColor:'text-orange-400',  gradBg:'from-orange-600/10',  border:'border-orange-500/30',  badgeText:'text-orange-400' },
  red:     { orb:'rgba(239,68,68,0.14)',   chip:'bg-red-500/10 text-white border-red-500/20',       iconColor:'text-red-400',     gradBg:'from-red-600/10',     border:'border-red-500/30',     badgeText:'text-red-400' },
  blue:    { orb:'rgba(59,130,246,0.14)',  chip:'bg-blue-500/10 text-white border-blue-500/20',     iconColor:'text-blue-400',    gradBg:'from-blue-600/10',    border:'border-blue-500/30',    badgeText:'text-blue-400' },
  indigo:  { orb:'rgba(99,102,241,0.14)',  chip:'bg-indigo-500/10 text-white border-indigo-500/20', iconColor:'text-indigo-400',  gradBg:'from-indigo-600/10',  border:'border-indigo-500/30',  badgeText:'text-indigo-400' },
  emerald: { orb:'rgba(16,185,129,0.14)',  chip:'bg-emerald-500/10 text-white border-emerald-500/20',iconColor:'text-emerald-400',gradBg:'from-emerald-600/10', border:'border-emerald-500/30', badgeText:'text-emerald-400' },
  rose:    { orb:'rgba(244,63,94,0.14)',   chip:'bg-rose-500/10 text-white border-rose-500/20',     iconColor:'text-rose-400',    gradBg:'from-rose-600/10',    border:'border-rose-500/30',    badgeText:'text-rose-400' },
}

const DEFAULT_FEATURE_ICONS = [FiZap, FiCpu, FiBookOpen, FiFileText, FiShield, FiRefreshCw]
const FAQ_KEYS = [1, 2, 3, 4, 5] as const
const STEP_KEYS = [1, 2, 3, 4] as const
const FEATURE_KEYS = [1, 2, 3, 4, 5, 6] as const
const USECASE_KEYS = [1, 2, 3, 4] as const
const REGULATION_KEYS = [1, 2, 3] as const

const METRICS: Record<string, { value: string; label: string }[]> = {
  jsa:             [{ value:'30초', label:'JSA 완성 시간' }, { value:'7단계', label:'평균 작업 분해' }, { value:'§36 준수', label:'산안법 자동 반영' }],
  tbm:             [{ value:'3분', label:'TBM 자료 생성' }, { value:'5개', label:'핵심 위험요인 검출' }, { value:'270만+', label:'사고사례 DB 검색' }],
  safetyEducation: [{ value:'2분', label:'PPTX 생성' }, { value:'30분', label:'교육 영상 분량' }, { value:'5종', label:'법정 교육 유형' }],
  workPermit:      [{ value:'6종', label:'작업허가서 양식' }, { value:'자동 분류', label:'작업 유형 AI 판정' }, { value:'즉시 출력', label:'Word 파일' }],
  accidentReport:  [{ value:'90초', label:'별지 30호 완성' }, { value:'14종', label:'재해 유형 자동 분류' }, { value:'95%+', label:'분류 신뢰도' }],
  chatbot:         [{ value:'3초', label:'평균 응답 시간' }, { value:'270만+', label:'사고사례 DB' }, { value:'매일', label:'법령 갱신' }],
  riskAssessment:  [{ value:'30초', label:'위험성평가표 생성' }, { value:'5×5', label:'KOSHA 매트릭스' }, { value:'전 업종', label:'지원 업종' }],
  imageAnalysis:   [{ value:'5초', label:'위험요인 검출' }, { value:'Top 5', label:'우선순위 식별' }, { value:'16건', label:'법령 근거 최대' }],
  corrective:      [{ value:'10초', label:'시정지시서 완성' }, { value:'자동', label:'법령 근거 매핑' }, { value:'자동', label:'기한 산정' }],
  eta:             [{ value:'2^N', label:'분기 시나리오 자동' }, { value:'ALARP', label:'위험도 분류' }, { value:'KOSHA', label:'P-87-2021 준거' }],
  fta:             [{ value:'MOCUS', label:'MCS 정확 계산' }, { value:'IEC 61025', label:'국제기준 준수' }, { value:'F-V 중요도', label:'기본사상 순위' }],
  safetyCost:      [{ value:'즉시', label:'계상금액 산정' }, { value:'9개 항목', label:'AI 권장 배분' }, { value:'2025-11호', label:'최신 고시 반영' }],
}

const COMPARISONS: Record<string, { aspect: string; manual: string; gpt: string; heinrich: string }[]> = {
  jsa:             [{ aspect:'작성 시간', manual:'2~3시간', gpt:'30분 (출처 없음)', heinrich:'30초' }, { aspect:'법령 근거', manual:'담당자 재량', gpt:'불확실·환각 가능', heinrich:'§36 자동 인용' }, { aspect:'위험도 매트릭스', manual:'수동 계산', gpt:'없음', heinrich:'5×5 자동 산정' }, { aspect:'출력 형식', manual:'사내 양식 수동', gpt:'없음', heinrich:'Excel/PDF 즉시' }],
  tbm:             [{ aspect:'자료 준비 시간', manual:'30~60분', gpt:'15분 (사고사례 없음)', heinrich:'3분' }, { aspect:'사고사례 반영', manual:'담당자 기억', gpt:'없음', heinrich:'270만+ DB 자동 검색' }, { aspect:'4컷 안전만화', manual:'불가', gpt:'없음', heinrich:'AI 자동 생성' }, { aspect:'법령 최신화', manual:'수동 확인', gpt:'학습 데이터 한계', heinrich:'매일 자동 갱신' }],
  safetyEducation: [{ aspect:'자료 제작 시간', manual:'8~16시간', gpt:'1~2시간 (법령 미반영)', heinrich:'8분 (PPTX+영상)' }, { aspect:'법령 준수 여부', manual:'담당자 의존', gpt:'불확실', heinrich:'시행규칙 §26 자동' }, { aspect:'영상·수료증', manual:'외부 업체 의뢰', gpt:'없음', heinrich:'자동 생성 패키지' }, { aspect:'비용', manual:'강사비 30만원/회', gpt:'출처 신뢰 불가', heinrich:'월 4,900원~' }],
  workPermit:      [{ aspect:'양식 분류', manual:'수동 선택', gpt:'불정확', heinrich:'AI 자동 분류' }, { aspect:'안전조치 체크리스트', manual:'담당자 작성', gpt:'없음', heinrich:'KOSHA P-94 기준 자동' }, { aspect:'결재 문서 출력', manual:'수동 편집', gpt:'없음', heinrich:'Word 즉시 출력' }, { aspect:'6종 양식 대응', manual:'6개 파일 관리', gpt:'없음', heinrich:'단일 인터페이스' }],
  accidentReport:  [{ aspect:'작성 시간', manual:'4~8시간', gpt:'1시간 (서식 부정확)', heinrich:'90초' }, { aspect:'별지 제30호 서식', manual:'수동 입력', gpt:'서식 불일치', heinrich:'표준 서식 그대로' }, { aspect:'재해 유형 분류', manual:'담당자 판단', gpt:'불확실', heinrich:'14종 AI 자동 분류' }, { aspect:'원인 분석', manual:'경험 의존', gpt:'출처 없음', heinrich:'3원인론 자동 도출' }],
  chatbot:         [{ aspect:'법령 출처 명시', manual:'—', gpt:'환각 가능·출처 불명', heinrich:'조항 번호 100% 명시' }, { aspect:'사고사례 연결', manual:'—', gpt:'없음', heinrich:'270만+ DB 자동 매칭' }, { aspect:'법령 최신화', manual:'—', gpt:'학습 데이터 한계', heinrich:'매일 03:00 자동 갱신' }, { aspect:'응답 시간', manual:'—', gpt:'2~5초', heinrich:'평균 3초 이내' }],
  riskAssessment:  [{ aspect:'위험요인 도출', manual:'2~4시간', gpt:'30분 (법령 미반영)', heinrich:'30초' }, { aspect:'5×5 매트릭스', manual:'수동 계산', gpt:'없음', heinrich:'KOSHA X-141 자동' }, { aspect:'엑셀 출력', manual:'수동 작성', gpt:'없음', heinrich:'인쇄용 즉시 출력' }, { aspect:'연간 재평가', manual:'처음부터 재작성', gpt:'없음', heinrich:'이력 기반 갱신' }],
  imageAnalysis:   [{ aspect:'위험요인 우선순위', manual:'육안 확인', gpt:'15~20개 나열 (순위 없음)', heinrich:'Top 5 우선순위 자동' }, { aspect:'법령 근거', manual:'담당자 조회', gpt:'없음', heinrich:'최대 16건 자동 인용' }, { aspect:'STOP WORK 판단', manual:'경험 의존', gpt:'없음', heinrich:'자동 권고' }, { aspect:'시정지시서 연결', manual:'별도 작성', gpt:'없음', heinrich:'원클릭 변환' }],
  corrective:      [{ aspect:'작성 시간', manual:'20~30분/건', gpt:'10분 (법령 부정확)', heinrich:'10초' }, { aspect:'위험도 기한 산정', manual:'담당자 재량', gpt:'없음', heinrich:'위험도별 자동 산정' }, { aspect:'법령 서식', manual:'수동 확인', gpt:'불정확', heinrich:'표준 서식 자동 준수' }, { aspect:'협력사 요청서', manual:'별도 작성', gpt:'없음', heinrich:'자동 변환' }],
  eta:             [{ aspect:'트리 생성', manual:'전문가 수작업 8h+', gpt:'미지원', heinrich:'AI 자동 생성' }, { aspect:'정량 계산', manual:'Excel 수동', gpt:'미지원', heinrich:'서버 사이드 검증' }, { aspect:'ALARP 분류', manual:'전문가 판단', gpt:'미지원', heinrich:'자동 라벨링' }, { aspect:'What-if 시뮬', manual:'재계산 반복', gpt:'미지원', heinrich:'슬라이더 즉시 반영' }],
  fta:             [{ aspect:'트리 생성', manual:'전문가 수작업 8h+', gpt:'미지원', heinrich:'AI 자동 분해' }, { aspect:'MCS 계산', manual:'MOCUS 수동', gpt:'미지원', heinrich:'정확 자동 계산' }, { aspect:'F-V 중요도', manual:'전문가 산출', gpt:'미지원', heinrich:'자동 순위 산출' }, { aspect:'IEC 61025 준수', manual:'전문가 의존', gpt:'미지원', heinrich:'표준 게이트 자동' }],
  safetyCost:      [{ aspect:'계상금액 계산', manual:'30분 (오류 빈번)', gpt:'불가', heinrich:'즉시 정확 계산' }, { aspect:'9개 항목 배분', manual:'경험 의존', gpt:'불가', heinrich:'AI 권장 배분 자동' }, { aspect:'고시 개정 반영', manual:'수동 확인', gpt:'불가', heinrich:'자동 추적' }, { aspect:'명세서 출력', manual:'수동 작성', gpt:'불가', heinrich:'DOCX/Excel 즉시' }],
}

type DemoConfig = { scenarios: { id: string; label: string; description: string; dataKey: string }[]; render: (data: unknown, scenarioId: string) => React.ReactNode } | null

function getDemoConfig(slug: string): DemoConfig {
  switch (slug) {
    case 'jsa':
      return { scenarios:[{ id:'default', label:'화학 반응기 밀폐공간 작업', description:'유해가스 위험, 밀폐공간 진입 작업', dataKey:'' }], render:(d)=><JSADemoResult data={d as Parameters<typeof JSADemoResult>[0]['data']} /> }
    case 'tbm':
      return { scenarios:[{ id:'default', label:'고층 외벽 도장 작업', description:'12층 곤돌라 작업, 도료 위험', dataKey:'' }], render:(d)=><TBMDemoResult data={d as Parameters<typeof TBMDemoResult>[0]['data']} /> }
    case 'safety-education':
      return { scenarios:[{ id:'default', label:'건설업 근로자 정기교육 (2분기)', description:'현장직 반기 12시간 법정 교육', dataKey:'' }], render:(d)=><SafetyEduDemoResult data={d as Parameters<typeof SafetyEduDemoResult>[0]['data']} /> }
    case 'work-permit':
      return { scenarios:[{ id:'default', label:'LPG 배관 용접 (화기 작업)', description:'화학공장 배관실, 폭발 위험', dataKey:'' }], render:(d)=><WorkPermitDemoResult data={d as Parameters<typeof WorkPermitDemoResult>[0]['data']} /> }
    case 'accident-report':
      return { scenarios:[{ id:'default', label:'3층 슬라브 개구부 추락 사고', description:'형틀목공 추락, 발목골절+요추 타박', dataKey:'' }], render:(d)=><AccidentReportDemoResult data={d as Parameters<typeof AccidentReportDemoResult>[0]['data']} /> }
    case 'chatbot':
      return {
        scenarios:[
          { id:'chat_1', label:'안전대 착용 의무 기준', description:'높이 2m 이상 작업, 산안법 근거', dataKey:'' },
          { id:'chat_2', label:'밀폐공간 작업 허가 절차', description:'맨홀 진입, 가스측정, 감시인', dataKey:'' },
          { id:'chat_3', label:'중대재해처벌법 관리체계', description:'50인 이상 의무, 경영책임자 처벌', dataKey:'' },
        ],
        render:(d,sid)=><ChatbotDemoResult data={d as Parameters<typeof ChatbotDemoResult>[0]['data']} scenarioId={sid} />
      }
    case 'risk-assessment':
      return { scenarios:[{ id:'default', label:'외벽 도장 작업 위험성평가', description:'5×5 KOSHA 매트릭스 자동 산정', dataKey:'' }], render:(d)=><RiskAssessmentDemoResult data={d as Parameters<typeof RiskAssessmentDemoResult>[0]['data']} /> }
    case 'image-analysis':
      return { scenarios:[{ id:'default', label:'프레스 구역 현장 사진 분석', description:'방호장치 이상 + 보호구 미착용', dataKey:'' }], render:(d)=><ImageAnalysisDemoResult data={d as Parameters<typeof ImageAnalysisDemoResult>[0]['data']} /> }
    case 'corrective':
      return { scenarios:[{ id:'default', label:'제조라인 정기 안전점검 결과', description:'4건 시정지시 (추락·화재·보호구·전기)', dataKey:'' }], render:(d)=><CorrectiveDemoResult data={d as Parameters<typeof CorrectiveDemoResult>[0]['data']} /> }
    case 'eta':
      return { scenarios:[{ id:'default', label:'LPG 배관 플랜지 누출 ETA', description:'2개 안전계층 → 4개 분기 시나리오 + 사건수 트리', dataKey:'' }], render:(d)=><ETADemoResult data={d as Parameters<typeof ETADemoResult>[0]['data']} /> }
    case 'fta':
      return { scenarios:[{ id:'default', label:'LPG 탱크 폭발사고 FTA', description:'6개 기본사상, 6개 MCS·F-V 중요도 + 결함수 트리', dataKey:'' }], render:(d)=><FTADemoResult data={d as Parameters<typeof FTADemoResult>[0]['data']} /> }
    case 'safety-cost':
      return { scenarios:[{ id:'default', label:'지식산업센터 신축공사 48억', description:'건축공사 · 9개 항목 AI 권장 배분', dataKey:'' }], render:(d)=><SafetyCostDemoResult data={d as Parameters<typeof SafetyCostDemoResult>[0]['data']} /> }
    default: return null
  }
}

export default function FeaturePage({ id, slug, HeroIcon, accent, featureIcons = DEFAULT_FEATURE_ICONS, related = [] }: FeaturePageProps) {
  const { t } = useTranslation()
  const acc = ACCENT_PALETTE[accent] ?? ACCENT_PALETTE.amber
  const k = (key: string) => `featurePages.${id}.${key}`
  const c = (key: string) => `featurePages.common.${key}`

  const canonicalUrl = `https://heinrich.dasancms.co.kr/features/${slug}/`
  const ogImageUrl = `https://heinrich.dasancms.co.kr/og/${slug}.png`
  const keywordsRaw = t(k('metaKeywords'), { defaultValue: '' })

  const metrics = METRICS[id] ?? []
  const comparison = COMPARISONS[id] ?? []
  const demoConfig = getDemoConfig(slug)

  const faqJsonLd = useMemo(() => ({ '@context':'https://schema.org', '@type':'FAQPage', mainEntity: FAQ_KEYS.map((n) => ({ '@type':'Question', name:t(k(`faq${n}Q`)), acceptedAnswer:{ '@type':'Answer', text:t(k(`faq${n}A`)) } })) }), [t, id])
  const softwareJsonLd = useMemo(() => ({ '@context':'https://schema.org', '@type':'SoftwareApplication', name:t(k('pageTitle')), applicationCategory:'BusinessApplication', applicationSubCategory:'Industrial Safety AI', operatingSystem:'Web', description:t(k('metaDescription')), url:canonicalUrl, image:ogImageUrl, inLanguage:['ko','en'], offers:{ '@type':'Offer', price:'0', priceCurrency:'KRW' }, publisher:{ '@type':'Organization', name:'Heinrich', url:'https://heinrich.dasancms.co.kr/' } }), [t, id, canonicalUrl, ogImageUrl])
  const serviceJsonLd = useMemo(() => ({ '@context':'https://schema.org', '@type':'Service', name:t(k('pageTitle')), description:t(k('metaDescription')), serviceType:'산업안전 AI SaaS', areaServed:{ '@type':'Country', name:'KR' }, provider:{ '@type':'Organization', name:'다산컨설팅', url:'https://dasancms.co.kr/' }, offers:{ '@type':'Offer', price:'0', priceCurrency:'KRW', availability:'https://schema.org/InStock', url:'https://heinrich.dasancms.co.kr/pricing/' } }), [t, id])
  const howToJsonLd = useMemo(() => ({ '@context':'https://schema.org', '@type':'HowTo', name:t(k('pageTitle')), description:t(k('metaDescription')), totalTime:'PT5M', step: STEP_KEYS.map((n) => ({ '@type':'HowToStep', position:n, name:t(k(`step${n}Title`)), text:t(k(`step${n}Desc`)) })) }), [t, id])
  const breadcrumbJsonLd = useMemo(() => ({ '@context':'https://schema.org', '@type':'BreadcrumbList', itemListElement:[ { '@type':'ListItem', position:1, name:'홈', item:'https://heinrich.dasancms.co.kr/' }, { '@type':'ListItem', position:2, name:'기능', item:'https://heinrich.dasancms.co.kr/#features/' }, { '@type':'ListItem', position:3, name:t(k('heroBadge')), item:canonicalUrl } ] }), [t, id, canonicalUrl])

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>{t(k('pageTitle'))}</title>
        <meta name="description" content={t(k('metaDescription'))} />
        {keywordsRaw && <meta name="keywords" content={keywordsRaw} />}
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="ko" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t(k('ogTitle'))} />
        <meta property="og:description" content={t(k('ogDescription'))} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={t(k('ogTitle'))} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t(k('ogTitle'))} />
        <meta name="twitter:description" content={t(k('ogDescription'))} />
        <meta name="twitter:image" content={ogImageUrl} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden pt-32 pb-16 md:pt-36 md:pb-20 border-b border-white/[0.06]">
        <div className="absolute inset-0 pointer-events-none hero-keep-orb" aria-hidden="true">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[44rem] h-[28rem] rounded-full blur-3xl"
            style={{ background: `radial-gradient(ellipse, ${acc.orb} 0%, transparent 65%)` }} />
        </div>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold border ${acc.chip} mb-6`}>
            <HeroIcon size={12} /> {t(k('heroBadge'))}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
            {t(k('heroH1Top'))}<br /><span className="text-white">{t(k('heroH1Highlight'))}</span>
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 leading-relaxed max-w-3xl mb-10">
            {renderWithBrand(t(k('heroSubtitle')))}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors">
              무료로 시작하기 <FiArrowRight size={14} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-white/[0.04] border border-white/[0.12] text-zinc-200 hover:bg-white/[0.08] hover:border-white/[0.20] transition-colors">
              요금제 보기
            </Link>
          </div>
        </div>
      </section>

      {/* ── Metrics Bar ── */}
      {metrics.length > 0 && (
        <section className="py-8 border-b border-white/[0.06] bg-zinc-900/40">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-6 divide-x divide-white/[0.06]">
              {metrics.map((m) => (
                <div key={m.label} className="text-center px-4">
                  <div className={`text-3xl md:text-4xl font-bold mb-1 ${acc.badgeText}`}>{m.value}</div>
                  <div className="text-xs text-zinc-500">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Intro ── */}
      <section className="py-20 md:py-24 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-intro`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-[6fr_5fr] gap-10 lg:gap-14 items-center">
            <div>
              <h2 id={`${slug}-intro`} className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
                {renderWithBrand(t(k('introTitle')))}
              </h2>
              <p className="text-base md:text-lg text-zinc-300 leading-[1.85] whitespace-pre-line">
                {renderWithBrand(t(k('introBody')))}
              </p>
            </div>
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-white/[0.08]">
              <img
                src={`/features/${slug}-intro.png`}
                alt={t(k('heroBadge'))}
                className="w-full h-full object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/assets/section/feature-context.jpg' }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Demo Section ── */}
      {slug === 'safety-education' ? (
        <section className="py-20 md:py-24 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-demo`}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <motion.div {...inView} className="text-center mb-10">
              <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>실제 결과물 미리보기</span>
              <h2 id={`${slug}-demo`} className="text-3xl md:text-4xl font-bold text-white mb-3">AI가 생성한 교육 자료 패키지</h2>
              <p className="text-zinc-400 text-sm">PPTX 슬라이드 · 교육 영상 · 이해도 평가 문제집 — 8분 안에 자동 생성</p>
            </motion.div>
            <SafetyEduShowcase />
            <p className="text-center text-xs text-zinc-600 mt-4">
              실제 Heinrich 서비스에서 생성된 결과물 샘플입니다. 로그인 후 원하는 교육 유형으로 직접 생성해보세요.
            </p>
          </div>
        </section>
      ) : demoConfig && (
        <section className="py-20 md:py-24 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-demo`}>
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <motion.div {...inView} className="text-center mb-10">
              <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>실제 결과물 미리보기</span>
              <h2 id={`${slug}-demo`} className="text-3xl md:text-4xl font-bold text-white mb-3">직접 확인해보세요</h2>
              <p className="text-zinc-400 text-sm">실제 생성된 결과물입니다. 시나리오를 선택하고 AI 분석을 실행해보세요.</p>
            </motion.div>
            <FeatureDemo slug={slug} scenarios={demoConfig.scenarios} renderResult={demoConfig.render} />
            <p className="text-center text-xs text-zinc-600 mt-3">
              실제 Heinrich 서비스에서 생성된 결과물 샘플입니다. 로그인 후 직접 생성하면 더 정밀한 결과를 얻을 수 있습니다.
            </p>
          </div>
        </section>
      )}

      {/* ── 핵심 기능 6카드 ── */}
      <section className="py-20 md:py-28 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-features`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>{t(c('featureGridBadge'))}</span>
            <h2 id={`${slug}-features`} className="text-3xl md:text-4xl font-bold text-white">{renderWithBrand(t(k('introTitle')))}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] rounded-md overflow-hidden border border-white/[0.06]">
            {FEATURE_KEYS.map((n, i) => {
              const Icon = featureIcons[i] ?? DEFAULT_FEATURE_ICONS[i]!
              return (
                <div key={n} className="bg-zinc-950 p-7 hover:bg-zinc-900 transition-colors">
                  <Icon size={20} className="text-zinc-400 mb-4" />
                  <h3 className="text-base font-semibold text-white mb-2">{t(k(`feature${n}Title`))}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{renderWithBrand(t(k(`feature${n}Desc`)))}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── 4단계 워크플로우 ── */}
      <section className="py-20 md:py-28 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-workflow`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div {...inView} className="text-center mb-14">
            <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>{t(c('stepBadge'))}</span>
            <h2 id={`${slug}-workflow`} className="text-3xl md:text-4xl font-bold text-white">{t(k('heroH1Highlight'))}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEP_KEYS.map((n) => (
              <div key={n} className="relative">
                <div className={`text-xs font-mono tracking-wider mb-3 ${acc.iconColor}`}>STEP {String(n).padStart(2, '0')}</div>
                <h3 className="text-base font-semibold text-white mb-2">{t(k(`step${n}Title`))}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{renderWithBrand(t(k(`step${n}Desc`)))}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison Table ── */}
      {comparison.length > 0 && (
        <section className="py-20 md:py-24 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-compare`}>
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <motion.div {...inView} className="text-center mb-10">
              <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>비교 분석</span>
              <h2 id={`${slug}-compare`} className="text-3xl md:text-4xl font-bold text-white">왜 Heinrich인가</h2>
            </motion.div>
            <div className="rounded-xl border border-white/[0.08] overflow-hidden">
              <div className="grid grid-cols-4 bg-zinc-800/60 text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                <div className="px-4 py-3 border-r border-white/[0.06]">항목</div>
                <div className="px-4 py-3 border-r border-white/[0.06] text-center">수기 작성</div>
                <div className="px-4 py-3 border-r border-white/[0.06] text-center">일반 ChatGPT</div>
                <div className={`px-4 py-3 text-center font-bold ${acc.badgeText}`}>Heinrich ✓</div>
              </div>
              {comparison.map((row, i) => (
                <div key={i} className={`grid grid-cols-4 text-sm border-t border-white/[0.06] ${i % 2 === 0 ? 'bg-zinc-950' : 'bg-zinc-900/30'}`}>
                  <div className="px-4 py-3 text-zinc-400 border-r border-white/[0.06] font-medium">{row.aspect}</div>
                  <div className="px-4 py-3 text-zinc-500 border-r border-white/[0.06] text-center">{row.manual}</div>
                  <div className="px-4 py-3 text-zinc-500 border-r border-white/[0.06] text-center">{row.gpt}</div>
                  <div className={`px-4 py-3 text-center font-semibold ${acc.badgeText}`}>{row.heinrich}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 사용 시나리오 + 법령 근거 ── */}
      <section className="py-20 md:py-28 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-trust`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <motion.div {...inView}>
            <span className={`inline-block text-xs font-bold uppercase tracking-widest mb-4 ${acc.iconColor}`}>{t(c('useCaseBadge'))}</span>
            <h2 id={`${slug}-trust`} className="text-2xl md:text-3xl font-bold text-white mb-6">{t(c('useCaseBadge'))}</h2>
            <ul className="space-y-3">
              {USECASE_KEYS.map((n) => (
                <li key={n} className="flex gap-3">
                  <FiCheck className={`mt-1 shrink-0 ${acc.iconColor}`} size={16} />
                  <span className="text-zinc-300 leading-relaxed">{t(k(`useCase${n}`))}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <div>
            <span className={`inline-block text-xs font-mono tracking-wider mb-4 ${acc.iconColor}`}>{t(c('regulationBadge'))}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">{t(c('regulationBadge'))}</h2>
            <ul className="space-y-2">
              {REGULATION_KEYS.map((n) => (
                <li key={n} className={`flex gap-3 border-l-2 pl-4 py-2 ${acc.border}`}>
                  <FiBookOpen className="mt-0.5 shrink-0 text-zinc-500" size={14} />
                  <span className="text-zinc-200 text-sm leading-relaxed">{t(k(`regulation${n}`))}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 md:py-28 bg-zinc-950 border-t border-white/[0.06]" aria-labelledby={`${slug}-faq`}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.h2 {...inView} id={`${slug}-faq`} className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            {t(c('faqTitle'))}
          </motion.h2>
          <div className="space-y-3">
            {FAQ_KEYS.map((n) => (
              <details key={n} className="group rounded-md border border-white/[0.08] bg-white/[0.02] overflow-hidden">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5 hover:bg-white/[0.03] transition">
                  <span className="text-base font-semibold text-white pr-4">{t(k(`faq${n}Q`))}</span>
                  <FiArrowRight className="mt-1 shrink-0 text-zinc-500 group-open:rotate-90 transition-transform" size={16} />
                </summary>
                <div className="px-5 pb-5 text-sm text-zinc-300 leading-relaxed">{renderWithBrand(t(k(`faq${n}A`)))}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 관련 기능 ── */}
      {related.length > 0 && (
        <section className="py-20 md:py-24 bg-zinc-950 border-t border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <motion.div {...inView} className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t(c('relatedTitle'))}</h2>
              <p className="text-zinc-400">{t(c('relatedSubtitle'))}</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/features/${r.slug}`}
                  className="group rounded-md border border-white/[0.08] bg-white/[0.02] p-5 hover:border-white/[0.18] hover:bg-white/[0.04] transition">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${acc.iconColor} mb-2`}>
                    <Brand /> · {r.slug.toUpperCase()}
                  </span>
                  <h3 className="text-base font-semibold text-white mb-2">{t(r.labelKey)}</h3>
                  <span className="inline-flex items-center gap-1 text-sm text-zinc-400 group-hover:text-white transition">
                    {t(c('ctaTryNow'))} <FiArrowRight size={14} className="group-hover:translate-x-0.5 transition" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Final CTA ── */}
      <section className="py-24 md:py-32 border-t border-white/[0.06] bg-zinc-950">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">{t(c('footerCtaTitle'))}</h2>
          <p className="text-base md:text-lg text-zinc-400 mb-10">{t(c('footerCtaDesc'))}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors">
              {t(c('footerCtaStart'))} <FiArrowRight size={14} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm font-semibold bg-white/[0.04] border border-white/[0.12] text-zinc-200 hover:bg-white/[0.08] hover:border-white/[0.20] transition-colors">
              {t(c('footerCtaCompare'))}
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
